import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppColors } from "@/styles/colors";
import { Box, Card, Flex, Text, RadioGroup, HStack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { productCreationSchema } from "../schema/products.schema";
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { TextArea } from "@/components/text-area/TextArea";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import {
  handleEditProductHandler,
  fetchProductByIdHandler,
} from "../schema/handler-functions";
import { isEmpty } from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";
import {
  MeasurementUnits,
  MeasurementUnitsLabel,
  TipoInsumos,
} from "@/constants/enums";

export const ProductEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const [loadingEditProduct, setLoadingEditProduct] = useState<boolean>(false);
  const [loadingProduct, setLoadingProduct] = useState<boolean>(true);
  const [selectedTipoInsumo, setSelectedTipoInsumo] = useState<string>(
    TipoInsumos.Fertilizante.toString(),
  );

  const {
    getValues,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(productCreationSchema),
    defaultValues: {
      tipoProducto: 1, // Fertilizante por defecto
      activo: true, // Activo por defecto
    },
  });

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  // Cargar datos del producto
  useEffect(() => {
    if (id) {
      fetchProductData();
    }
  }, [id]);

  const fetchProductData = async () => {
    if (!id) return;

    await fetchProductByIdHandler({
      id,
      setLoadingProduct,
      reset,
      setSelectedTipoInsumo,
      navigate,
      fetchPermissions,
    });
  };

  const handleEditProduct = async () => {
    if (!id) return;

    await handleEditProductHandler({
      id,
      getValues,
      setLoadingEditProduct,
      navigate,
      fetchPermissions,
      tipoProducto: Number(selectedTipoInsumo),
    });
  };

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTS);
  };

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { tipoProducto, nombre, descripcion } = watchedValues || {};

    return !tipoProducto || isEmpty(nombre) || isEmpty(descripcion);
  }, [watchedValues]);

  const radioItems = [
    {
      label: "Fertilizante",
      value: TipoInsumos.Fertilizante.toString(),
    },
    {
      label: "Combustible",
      value: TipoInsumos.Combustible.toString(),
    },
  ];

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar producto"
        addButtonLabel="Guardar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleEditProduct}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingEditProduct || loadingProduct}
        loading={loadingEditProduct || loadingProduct}
      />

      {!loadingProduct && (
        <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            {/* Tipo de insumo */}
            <Flex flexDir="column" gap="12px" marginBottom="24px">
              <Text fontWeight={700} fontSize="16px">
                Tipo de insumo
              </Text>
              <RadioGroup.Root
                value={selectedTipoInsumo}
                onValueChange={(e) => e.value && setSelectedTipoInsumo(e.value)}
              >
                <HStack gap="6">
                  {radioItems.map((item) => (
                    <RadioGroup.Item key={item.value} value={item.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </HStack>
              </RadioGroup.Root>
            </Flex>

            <Flex gap="16px" align="start" marginBottom="24px">
              {/* Nombre */}
              <Box width="250px">
                <InputField
                  inputName="nombre"
                  labelText="Nombre"
                  control={control}
                  placeholder="Ingrese el nombre del producto"
                  disabled={loadingEditProduct}
                  required
                />
              </Box>

              {/* N° de Admis */}
              <Box width="250px">
                <InputField
                  inputName="numAdmis"
                  labelText="N° Admis"
                  control={control}
                  placeholder="Ingrese N° ADMIS"
                  disabled={loadingEditProduct}
                />
              </Box>

              {/* Unidad de medida */}
              <Box width="250px">
                <SelectComponent
                  items={Object.values(MeasurementUnits).map((unit) => ({
                    label: MeasurementUnitsLabel[unit as MeasurementUnits],
                    value: unit,
                  }))}
                  control={control}
                  name="unidadMedida"
                  labelText="Unidad de medida"
                  disabled={loadingEditProduct}
                  required
                />
              </Box>

              {/* Estado */}
              <Box width="200px">
                <SelectComponent
                  name="activo"
                  labelText="Estado"
                  placeholder="Seleccionar estado"
                  control={control}
                  disabled={loadingEditProduct}
                  required
                  clearable={false}
                  items={[
                    { label: "Activo", value: true },
                    { label: "Desactivado", value: false },
                  ]}
                />
              </Box>
            </Flex>
            {/* Descripción */}
            <Flex flexDir="column" gap="12px" marginTop="24px">
              <Text fontWeight={700} fontSize="20px">
                Descripción
              </Text>
              <TextArea
                errors={errors}
                inputName="descripcion"
                maxLength={100}
                register={register}
                placeholder="Ingrese una descripción del producto"
                helperText="Máximo 100 caracteres"
                disabled={loadingEditProduct}
                required
              />
            </Flex>
          </Card.Body>
        </Card.Root>
      )}
    </Flex>
  );
};
